import { useCallback, useEffect, useRef, useState } from 'react'

import { tabIsVisible } from './music.js'

// The portal's second hand on the music controls. P6-189 (M5), under ADR-0006.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 IT RENDERS THE SERVICE'S ANSWER. IT NEVER PRE-JUDGES ONE.
// ═══════════════════════════════════════════════════════════════════════════
//
// ADR-0006 D4: *"A command from the portal is the same operation the slash
// command performs, with the same preconditions, applied as the same member...
// The portal never gains a power Discord lacks."* `music/cogs/control.py`
// enforces every precondition and is deliberately STRICTER than Discord in three
// places, which it lists rather than leaving to be discovered.
//
// The consequence for this file is a rule with teeth: **no button is disabled to
// simulate a precondition.** Not "you are not in the voice channel", not "that
// is not your track", not "nothing is playing" — every one of those is a
// question only the service can answer, and every one of them comes back as a
// `200` with a label. A greyed button asserting one of them from here would be
// this page guessing at somebody's voice state from a projection that is up to
// two seconds old, and being wrong in the direction that silently removes a
// capability.
//
// ⚠️ THE ONE THING THAT IS ALLOWED TO DISABLE A BUTTON IS THE TUNNEL, and only
// because ADR-0006 D7 makes it a designed state rather than a guess: *"The
// control buttons report the tunnel as unreachable, plainly, and Discord's own
// controls are unaffected — they never went through the tunnel."* That is
// measured by asking, not inferred.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 A REFUSAL IS AN ANSWER, AND IT IS RENDERED LIKE ONE
// ═══════════════════════════════════════════════════════════════════════════
//
// Spec §5: *"A refusal is a `200` with a labeled outcome, never a `4xx` — the
// member did nothing wrong."* So nothing in this file treats `refused` as an
// error: no red, no "something went wrong", no retry prompt. CLAUDE.md's
// Register section is explicit that plain language is what belongs in anything
// about somebody's picks, and `music/utils/control.py` already writes the
// sentence in it — *"`detail` is a member-facing sentence in this repo's
// Register... and `data` carries the structured extras so the portal never has
// to parse the sentence."* We show `detail`.
//
// ═══════════════════════════════════════════════════════════════════════════
// 🔴 WHAT THIS MODULE MUST NEVER DO, AND WHY IT IS WRITTEN HERE
// ═══════════════════════════════════════════════════════════════════════════
//
// `src/portal/music.js`'s `NOT_YET_PUBLISHED` forbids the client synthesising
// `shared.control` or `shared.last_commands` onto the projection, because *"a
// null reads as a value"*. **That rule is unchanged by this file and this file
// is the obvious place it would be broken.** Reachability lives here, in its own
// state, read from its own endpoint; it is never written back onto the blob.
// `src/test/portal/music-window.test.jsx` still walks that list against what
// `buildMusic` returns.

/** The Function's control route. `index.js` answers the probe; `[op].js` the
 * commands. One constant so the two callers cannot disagree about the path. */
export const CONTROL_ENDPOINT = '/api/music/control'

/**
 * How often the panel re-asks whether the tunnel is up, while it is on screen.
 *
 * ⚠️ SLOWER THAN THE PROJECTION'S `POLL_MS` (2s) AND FASTER THAN IT LOOKS LIKE
 * IT NEEDS TO BE, for one reason: the acceptance criterion. Spec §9.6, the kill
 * test — *"Restore it: buttons return without a reload."* That is a promise
 * about a WORST CASE, so the interval is the worst case, and five seconds is
 * what makes "the buttons came back" read as immediate rather than as something
 * you wait for.
 *
 * The cost is one small request every five seconds per open Music pill, over a
 * tunnel whose in-handler cost P6-188 measured at a tenth of a millisecond. It
 * is bounded the same way the projection poll is: the panel unmounts when the
 * pill changes, and a hidden tab does not poll at all.
 */
export const HEALTH_POLL_MS = 5_000

/**
 * The reachability states, and none of them is a boolean.
 *
 * 🔴 `PROBING` IS NOT `OFFLINE`, and the distinction is `music.js`'s own —
 * *"For the first moment of every load NOTHING HAS FAILED. A panel that said
 * 'music is unavailable' there would be the portal reporting an outage that has
 * not happened."* On a polled surface it would say it every time somebody opened
 * the pill.
 *
 * 🔴 AND `UNCONFIGURED` IS NOT `OFFLINE` EITHER. Preview deployments hold none
 * of the three credentials, deliberately, so that a preview build cannot issue a
 * real command into the live voice channel. Rendering that as "music's box is
 * offline" would be this page reporting an outage on vegapunk because somebody
 * opened a branch preview.
 */
export const PROBING = 'probing'
export const LIVE = 'live'
export const CONNECTING = 'connecting'
export const OFFLINE = 'offline'
export const DENIED = 'denied'
export const BROKEN = 'broken'
export const UNCONFIGURED = 'unconfigured'

/** Reachability states in which a command may be attempted. Everything else
 * disables the controls and says why. */
const COMMANDABLE = new Set([LIVE, CONNECTING])

export function controlsAreLive(state) {
  return COMMANDABLE.has(state)
}

/**
 * What each state says, in words.
 *
 * 🔴 A LOOKUP, NEVER AN INTERPOLATION — `respond.js`'s `STAGE_QUESTION` rule.
 * The only strings this module can put on a page are the literals below, so a
 * `why` token this version has never heard of degrades to a sentence that claims
 * nothing rather than rendering a raw word.
 *
 * ⚠️ AND `offline` IS THE ONLY ONE THAT MENTIONS VEGAPUNK'S BOX. ADR-0006 D7 is
 * a promise about one specific failure — the home line rebooting — and spec §6
 * asks for exactly this separation: *"our [job] is to make the Function's tunnel
 * errors legible enough that 'the tunnel is down' and 'music refused' never read
 * the same."* A misconfigured credential is not a reboot, and telling somebody
 * the box is off when a variable is unset sends them to look at the wrong thing.
 */
const STATE_SENTENCE = Object.freeze({
  [CONNECTING]: 'Music is still connecting to Discord. Controls may not land yet.',
  [OFFLINE]: 'Music’s box is not answering, so the controls are off. Everything still works in Discord.',
  [DENIED]: 'The site could not prove itself to the music service. This is our problem, not yours — the controls work in Discord.',
  [BROKEN]: 'The music service answered with something we could not read. The controls work in Discord.',
  [UNCONFIGURED]: 'The music controls are not set up on this deployment. Everything still works in Discord.',
})

export function stateSentence(state) {
  return Object.hasOwn(STATE_SENTENCE, state) ? STATE_SENTENCE[state] : null
}

/** The `why` tokens the Function can send, mapped onto the states above.
 * `functions/_lib/music-control.js::FAILURE` is the other half of this contract
 * — gotcha 21 says two implementations of one contract have to be asserted
 * against each other, and `src/test/portal/music-controls.test.jsx` drives the
 * real Function's tokens through this map rather than restating them. */
const WHY_STATE = Object.freeze({
  unreachable: OFFLINE,
  denied: DENIED,
  unavailable: BROKEN,
  broken: BROKEN,
})

/**
 * What a command that never produced an outcome says, in words.
 *
 * ⚠️ `unavailable` IS ITS OWN SENTENCE HERE AND FOLDS INTO `BROKEN` ABOVE, and
 * the asymmetry is deliberate. As a *reachability* state "the service has no
 * such op" is meaningless — the tunnel is plainly up. As the answer to a button
 * somebody pressed it is the most useful thing that could be said, and it is the
 * live case: `search` is deferred on the service side (MS1), so a client that
 * offered it would land here.
 */
const COMMAND_FAILURE = Object.freeze({
  unreachable: 'Music’s box did not answer, so nothing happened. Try again in a moment.',
  denied: 'The site could not prove itself to the music service, so nothing happened. This is our problem, not yours.',
  unavailable: 'That control is not available yet.',
  broken: 'The music service could not answer that, so nothing happened.',
})

/** A generic sentence for an answer this version cannot read at all. It claims
 * only what is safe to claim: we do not know whether it landed. */
const UNREADABLE = 'We could not tell what happened to that. Check Discord.'

/**
 * The longest query this box will send. P6-198.
 *
 * ⚠️ IT IS A THIRD COPY OF 512 AND THAT IS THE POINT OF NAMING IT. The other
 * two are `control.require_text`'s default on the service side and
 * `buildCommandBody`'s string check in the Function; both answer a value over
 * the cap with a **400**, which `sendControl` reads as `broken` — *"our bug,
 * go and read a log"*. It is our bug: this is the only client, so the input is
 * capped where somebody is typing rather than refused three hops later.
 */
export const MAX_QUERY_CHARS = 512

/**
 * The two words a `search` can answer with, and the ONLY outcome words this
 * repo spells out. P6-198.
 *
 * 🔴 EVERY OTHER OUTCOME IS PASSED THROUGH AND NEVER COMPARED — `skipped`,
 * `refused`, `already_liked` reach the panel as `detail` and a `data-outcome`
 * attribute, so this side has no opinion about them and cannot drift. These two
 * are different because the panel has to tell one from the other: `no_results`
 * is *"we looked and YouTube had nothing"* and arrives with an empty list, while
 * `results` with an empty list means the service claimed five and none of them
 * survived the wire. Those need different sentences.
 *
 * ⚠️ THEY ARE A CROSS-REPO LITERAL AND NO TEST CAN SPAN THE TWO SIDES —
 * `music/utils/control.py`'s `RESULTS` and `NO_RESULTS` are the other half, and
 * P6-188's port number is the precedent: paired cross-comments are the only
 * mitigation either side has. The failure if they drift is fail-OPEN and quiet
 * (the unreadable sentence stops appearing), which is why it is written down
 * here rather than left as a literal three functions down.
 */
export const RESULTS = 'results'
export const NO_RESULTS = 'no_results'

/**
 * The five candidates out of a `search` answer, or `null` if it was not one.
 *
 * 🔴 `null` AND `[]` ARE DIFFERENT ANSWERS AND THE PANEL BRANCHES ON BOTH.
 * `null` means *this outcome carried no result list at all* — a skip, a
 * refusal, an enqueue. `[]` means *the service looked and found nothing*, which
 * `music/utils/control.py` spells `no_results` and sends **with** the empty
 * field precisely so a reader never has to check which word arrived before it
 * can iterate. Collapsing the two here would undo that one layer up.
 *
 * ⚠️ THE SHAPE IS CHECKED AT THIS EDGE TOO, and it is not a duplicate of the
 * service's own check — `music.js` states the rule for the projection and it is
 * the same one: the far side asserts what came out of the resolver, this
 * asserts what came over the wire, and there is a Function, a tunnel and a
 * browser in between.
 *
 * 🔴 A ROW WITH NO `url` IS DROPPED RATHER THAN RENDERED DISABLED. The url is
 * the whole of what a row is *for* — it is the value that goes back as
 * `enqueue {url}` — so a row without one is a button that cannot do anything.
 * `music/utils/resolver.py::_candidate_from` already drops those on its side
 * and logs; a row arriving here without one means the wire mangled it, and the
 * panel says so rather than showing a dead row.
 */
export function resultsOf(answer) {
  const rows = answer?.results
  if (!Array.isArray(rows)) return null
  const text = (value) =>
    typeof value === 'string' && value.trim() !== '' ? value : null
  return rows
    .filter((row) => row && typeof row === 'object' && !Array.isArray(row))
    .map((row) => ({
      title: text(row.title),
      channel: text(row.channel),
      // Passed through rather than coerced: `runtime()` in `music.js` is the
      // one place a duration becomes a string, and it already answers `null`
      // for anything it cannot read.
      duration_s: row.duration_s,
      url: text(row.url),
    }))
    .filter((row) => row.url !== null && row.title !== null)
}

/**
 * A fresh command id.
 *
 * 🔴 IT THROWS RATHER THAN FALLING BACK TO `Math.random`, and the reason is not
 * purity. `music/utils/control.py` keys its replay map on this id and *"a
 * repeated `id` returns the original outcome"* — so two commands that collide are
 * one command, and for `skip` that is a vote that silently did not count. A
 * weak generator turns a rare collision into a bug nobody can reproduce;
 * principle 5's fail-loud is the cheaper answer.
 *
 * ⚠️ `randomUUID` IS NOT UNIVERSALLY PRESENT ON A SECURE-CONTEXT-LESS PAGE, so
 * `getRandomValues` is tried second — it is the same entropy source, and the
 * version-4 layout is written out rather than assumed.
 */
export function newCommandId() {
  const source = globalThis.crypto
  if (typeof source?.randomUUID === 'function') return source.randomUUID()
  if (typeof source?.getRandomValues === 'function') {
    const bytes = source.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 0x0f) | 0x40
    bytes[8] = (bytes[8] & 0x3f) | 0x80
    const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  throw new Error('no cryptographic random source, so a command id cannot be minted safely')
}

/**
 * Ask the Function whether the tunnel is up. Resolves to a state, always.
 *
 * `fetchMusic`'s rule, and it binds for the same reason: this is polled, and a
 * rejecting branch would need a `catch` at every call site that one day somebody
 * forgets.
 */
export async function fetchReachability(fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return { state: BROKEN }
  let response
  try {
    response = await fetchImpl(CONTROL_ENDPOINT, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
  } catch {
    // No network at all. The tunnel is one of the things that could be down and
    // we cannot tell which, so this says the least alarming true thing.
    return { state: OFFLINE }
  }
  // 🔴 500 IS THE MISSING-CREDENTIAL BRANCH AND IT IS NOT AN OUTAGE. The route
  // answers 500 naming the variables when the three secrets are unset, which is
  // the whole of how a preview deployment is stopped from issuing real commands.
  if (response.status === 500) return { state: UNCONFIGURED }
  // Signed out. The panel above this one already says so; controls simply are
  // not offered.
  if (!response.ok) return { state: BROKEN }

  let payload
  try {
    payload = await response.json()
  } catch {
    return { state: BROKEN }
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return { state: BROKEN }

  if (payload.reachable !== true) {
    const why = payload.why
    // `Object.hasOwn`, never a bare index — an inherited property must not be
    // reachable through a value that came off the wire. #291's bug, one layer up.
    const state = typeof why === 'string' && Object.hasOwn(WHY_STATE, why) ? WHY_STATE[why] : BROKEN
    return { state }
  }
  // 🔴 `gateway` DOES NOT GATE THE BUTTONS, IT ANNOTATES THEM. The tunnel
  // answered, so the API is up; whether discord.py has finished connecting is a
  // second fact the service reports separately, and refusing to offer a control
  // because of it would be this page pre-judging an answer the service gives
  // for itself (D4). An unrecognised or absent value reads as LIVE for the same
  // reason: it is not evidence of anything.
  return { state: payload.gateway === 'connecting' ? CONNECTING : LIVE, asked_at: payload.asked_at }
}

/**
 * Send one command. Resolves to a result, always — never rejects.
 *
 * @param {string} op       one of the service's ops. NOT checked against a list
 *                          here: see the header, and `[op].js` on why a second
 *                          copy of somebody else's contract is the thing to
 *                          avoid.
 * @param {object} fields   op-specific fields (`position`, `from`/`to`, `key`,
 *                          `url`). The envelope is the Function's.
 * @param {string} id       🔴 REUSED ON RETRY, AND THAT IS THE WHOLE POINT.
 *                          ADR-0006 D4 makes the id the thing that recognises a
 *                          retry as the same command; spec §5 spells out the
 *                          consequence — *"a repeated `id` returns the original
 *                          outcome"*. A fresh id on retry is a SECOND command,
 *                          and for `skip` that is a second vote from one press.
 */
export async function sendControl(op, fields = {}, id = newCommandId(), fetchImpl = globalThis.fetch) {
  if (typeof fetchImpl !== 'function') return { id, state: BROKEN, message: UNREADABLE }
  let response
  try {
    response = await fetchImpl(`${CONTROL_ENDPOINT}/${op}`, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      // 🔴 NO `actor_id` AND NO `issued_at`. The Function mints both from the
      // verified session (ADR-0006 D3) and REFUSES a body that carries either —
      // so sending one here would not be a silent escalation, it would be a 400.
      // The id is ours because a retry has to be able to reuse it.
      body: JSON.stringify({ id, ...fields }),
    })
  } catch {
    return { id, state: OFFLINE, message: COMMAND_FAILURE.unreachable, retryable: true }
  }

  if (response.status === 500) {
    return { id, state: UNCONFIGURED, message: stateSentence(UNCONFIGURED), retryable: false }
  }
  let payload = null
  try {
    payload = await response.json()
  } catch {
    payload = null
  }
  if (!response.ok) {
    // A 400 from our own Function: the request could not be built or attributed.
    // It is never a member being told no — see `[op].js`.
    return { id, state: BROKEN, message: UNREADABLE, retryable: false }
  }
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return { id, state: BROKEN, message: UNREADABLE, retryable: false }
  }

  if (Object.hasOwn(payload, 'outcome')) {
    const outcome = payload.outcome
    if (!outcome || typeof outcome !== 'object' || Array.isArray(outcome)) {
      return { id, state: BROKEN, message: UNREADABLE, retryable: false }
    }
    // 🔴 `detail` IS THE SERVICE'S OWN SENTENCE AND IT IS SHOWN AS WRITTEN.
    // `music/utils/control.py`: *"`detail` is a member-facing sentence in this
    // repo's Register: plain language, no personality, and never a credential."*
    // Rendering our own paraphrase here would be a second vocabulary for the
    // same events, drifting from the day it was written.
    const detail = typeof outcome.detail === 'string' && outcome.detail.trim() !== ''
      ? outcome.detail
      : null
    return {
      id,
      // 🔴 A REFUSAL IS AN ANSWER, NOT A FAILURE. `refused` is one of the
      // service's outcomes and lands here exactly like `skipped` does; nothing
      // downstream may style it as an error.
      state: LIVE,
      answered: true,
      outcome: typeof outcome.outcome === 'string' ? outcome.outcome : null,
      reason: typeof outcome.reason === 'string' ? outcome.reason : null,
      replay: outcome.replay === true,
      message: detail ?? UNREADABLE,
      // 🔴 CARRIED, NOT INTERPRETED. `search` is the one op whose answer is a
      // LIST rather than a sentence (spec §5: *"`search` returns its five
      // results directly — the two-phase dance through the projection is
      // unnecessary once the path is HTTP"*), and this is where that list stops
      // being JSON. `null` for every other op, so nothing downstream has to
      // know which ops can produce one.
      results: resultsOf(outcome),
      retryable: false,
    }
  }

  const why = payload.why
  const message =
    typeof why === 'string' && Object.hasOwn(COMMAND_FAILURE, why) ? COMMAND_FAILURE[why] : UNREADABLE
  const state = typeof why === 'string' && Object.hasOwn(WHY_STATE, why) ? WHY_STATE[why] : BROKEN
  return {
    id,
    state,
    message,
    // 🔴 ONLY A TRANSPORT FAILURE OFFERS A RETRY, and the retry reuses the id.
    // `unavailable` is not retryable — the op does not exist in this version and
    // pressing again will not change that.
    retryable: why === 'unreachable' || why === 'broken',
  }
}

/**
 * Reachability, kept live while somebody is looking, plus one command at a time.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 ONE COMMAND IN FLIGHT FOR THE WHOLE PANEL, AND IT IS A CORRECTNESS
 *    DECISION RATHER THAN A COURTESY
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Every op that names a queue POSITION — `remove`, `move` — is addressed against
 * the projection the member is looking at, and that projection is up to
 * `POLL_MS` old. Two overlapping moves would both be computed against the same
 * stale list and the second would name a row that is no longer there: the
 * service bounds the position (P6-188 fixed exactly that hazard on its side), so
 * the worst case is a refusal rather than the wrong track — but a refusal for a
 * reason the member cannot see is worse than a button that waits.
 *
 * ⚠️ SO THE PANEL SERIALISES, AND SAYS SO BY DISABLING. The alternative — an
 * optimistic reorder rendered locally — would put a queue on the screen that the
 * service has not agreed to, which is the same class of claim as a progress bar
 * drawn from a position nothing stores. The projection is the truth and it
 * arrives within two seconds.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 THE SEARCH RESULTS LIVE IN `result` AND VANISH ON THE NEXT COMMAND, WHICH
 *    IS WHAT DISCORD DOES AND NOT A LIMITATION
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * P6-199. `send` clears `result` before every command, so tapping a row's
 * "Queue" button takes the five candidates off the screen and replaces them
 * with what happened. That is `MusicCog._deliver`'s `edit_original_response(
 * content=message, view=None)` — the select menu is REPLACED by the outcome,
 * in the one message the member was already looking at.
 *
 * ⚠️ SO THERE IS NO SECOND PIECE OF STATE HOLDING THE CANDIDATES, and that is
 * the point: a list kept alive beside `result` would outlive the search that
 * produced it, and a member would be able to tap a row minutes later — against
 * a `url` that was true when they searched. The service would still resolve it,
 * so nothing breaks; it would simply be a surface Discord does not have.
 */
export function useMusicControls() {
  const [state, setState] = useState(PROBING)
  const [result, setResult] = useState(null)
  const [busy, setBusy] = useState(false)
  // A ref rather than state: the send path reads it to decide whether to refuse
  // a second press, and a stale closure over `busy` would let two through.
  const inFlight = useRef(false)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    let timer = null

    const probe = () => {
      fetchReachability().then((answer) => {
        if (mounted.current) setState(answer.state)
      })
    }

    const stop = () => {
      if (timer !== null) {
        clearInterval(timer)
        timer = null
      }
    }

    // The same shape `useMusic` uses, and deliberately the same: guarded on
    // `timer === null` so a repeated `visible` event cannot stack a second
    // interval, and an immediate probe on the way back so the answer somebody
    // reads on returning is not the one from before they left.
    const sync = () => {
      if (!tabIsVisible()) {
        stop()
        return
      }
      if (timer === null) {
        probe()
        timer = setInterval(probe, HEALTH_POLL_MS)
      }
    }

    sync()
    const doc = globalThis.document
    doc?.addEventListener?.('visibilitychange', sync)
    return () => {
      mounted.current = false
      stop()
      doc?.removeEventListener?.('visibilitychange', sync)
    }
  }, [])

  const send = useCallback(async (op, fields = {}, id = newCommandId()) => {
    if (inFlight.current) return null
    inFlight.current = true
    setBusy(true)
    setResult(null)
    const answer = await sendControl(op, fields, id)
    if (mounted.current) {
      setResult({ ...answer, op, fields })
      // 🔴 A COMMAND'S OWN FATE UPDATES REACHABILITY, AND IT IS THE FASTEST
      // SIGNAL THERE IS. The poll is five seconds; a press that could not be
      // delivered knows *now*. An answered command proves the tunnel is up, so
      // it clears a stale `offline` without waiting for the next probe.
      if (answer.answered) setState((was) => (was === LIVE || was === CONNECTING ? was : LIVE))
      else setState(answer.state)
    }
    inFlight.current = false
    if (mounted.current) setBusy(false)
    return answer
  }, [])

  /** Press the same command again, with the same id — see `sendControl`. */
  const retry = useCallback(() => {
    if (!result || !result.retryable) return null
    return send(result.op, result.fields, result.id)
  }, [result, send])

  const dismiss = useCallback(() => setResult(null), [])

  return { state, live: controlsAreLive(state), busy, result, send, retry, dismiss }
}
